import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const SCOPE_ITEMS = [
  {
    icon: "ri-newspaper-line",
    title: "影视资讯",
    desc: "汇总欧美电影的上映动态、幕后花絮与行业趋势，帮助你第一时间了解新片动向。",
  },
  {
    icon: "ri-star-line",
    title: "深度影评",
    desc: "由编辑团队撰写的原创影评，从叙事、表演、视听等角度拆解作品，提供观影参考。",
  },
  {
    icon: "ri-list-check-2",
    title: "分类导览",
    desc: "按动作、剧情、科幻、悬疑等类型整理片单，配合筛选与排序，快速定位感兴趣的内容。",
  },
  {
    icon: "ri-fire-line",
    title: "热门推荐",
    desc: "结合点击量与评价数据生成的推荐榜单，让高分与高热度影片一目了然。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 欧美大片影视网",
    description:
      "了解欧美大片影视网的网站定位、内容范围与联系方式。我们专注欧美电影资讯、影评与推荐，为影迷提供可靠的观影参考。",
    keywords: "关于我们,欧美大片,欧美电影,网站介绍,联系方式",
  });

  return (
    <SiteLayout>
      {/* hero */}
      <section className="w-full bg-primary-950 text-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-label font-bold mb-4">
              <i className="ri-information-line" /> 关于欧美大片
            </span>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl leading-tight mb-4">
              一个为影迷而生的欧美电影内容平台
            </h1>
            <p className="text-sm md:text-base text-background-200/90 leading-loose">
              欧美大片影视网专注提供最新最全的欧美电影资讯、电影推荐与影评内容，帮助每一位影迷在观影前快速了解影片信息、在观影后发现更多值得回味的作品。
            </p>
          </div>
        </div>
      </section>

      {/* positioning */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-4">
              网站定位
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-loose">
              <p>
                我们把自己定位为一个<strong className="text-foreground-900">影视导航与内容展示平台</strong>。不同于追求时效的新闻站点，我们更希望成为影迷身边的一位"观影向导"——当你想找一部电影时，这里能提供清晰的分类入口与可信的推荐；当你看完一部电影时，这里能提供有深度的解读与延伸。
              </p>
              <p>
                目标用户是喜爱欧美电影、关注电影资讯与影评的普通观众与影评爱好者。无论你是想轻松解压，还是想挑战一部烧脑作品，都能在站内找到合适的内容。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              {[
                { num: "6", label: "核心页面" },
                { num: `${categories.length}`, label: "电影分类" },
                { num: "100%", label: "示范内容" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg bg-background-100 border border-background-200 p-5 text-center"
                >
                  <div className="font-heading font-extrabold text-2xl text-primary-600">
                    {stat.num}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-accent-50 border border-accent-200 p-6">
            <h2 className="font-heading font-bold text-base text-accent-900 mb-3 flex items-center gap-2">
              <i className="ri-mail-send-line" /> 联系方式
            </h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <i className="ri-mail-line text-accent-600 text-lg mt-0.5" />
                <span>
                  <span className="block text-foreground-400 text-xs">邮箱</span>
                  <span className="text-foreground-800">contact@oumeidapian.example</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-wechat-line text-accent-600 text-lg mt-0.5" />
                <span>
                  <span className="block text-foreground-400 text-xs">微信公众号</span>
                  <span className="text-foreground-800">欧美大片影视网</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-accent-600 text-lg mt-0.5" />
                <span>
                  <span className="block text-foreground-400 text-xs">回复时间</span>
                  <span className="text-foreground-800">工作日 10:00 - 18:00</span>
                </span>
              </li>
            </ul>
            <p className="text-xs text-foreground-500 mt-5 leading-relaxed">
              <i className="ri-error-warning-line mr-1 text-accent-600" />
              以上联系方式为示范信息，用于展示页面功能。
            </p>
          </div>
        </div>
      </section>

      {/* scope */}
      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-6">
            内容范围说明
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {SCOPE_ITEMS.map((item) => (
              <div
                key={item.title}
                className="rounded-lg bg-background-50 border border-background-200 p-5"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 mb-4">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="font-heading font-bold text-base text-foreground-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* form */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <FeedbackForm />
        </div>
      </section>
    </SiteLayout>
  );
}